import { Badge } from '@dixa/lib-shared-habla';
import { Message } from 'lib-shared-legacy-types';

import TextMessage from 'components/conversation/components/messages/TextMessage';
import { useAppSelector } from 'utils/redux/useAppSelector';

import {
  isMessageSupported,
  SupportedMessage,
} from '../../utils/conversationPreviewDefinitions';

import styles from './conversationPreviewMessages.module.scss';
import {
  ConversationPreviewRecipients,
  hasEmailRecipients,
} from './conversationPreviewRecipients';

type ConversationPreviewMessagesProps = {
  messages: Array<Readonly<Message | SupportedMessage>>;
  requesterId: string | null | undefined;
  /** Whether to include internal notes. Defaults to true. */
  showNotes?: boolean;
  /** Whether to show each message's email recipients. Defaults to false. */
  showRecipients?: boolean;
};

export const ConversationPreviewMessages = ({
  messages,
  requesterId,
  showNotes = true,
  showRecipients = false,
}: ConversationPreviewMessagesProps): JSX.Element => {
  const currentUserId = useAppSelector(
    (state) => state.settings.profile.profile.id,
  );

  const supportedMessages = messages.filter(
    (message): message is Readonly<SupportedMessage> =>
      isMessageSupported(message) &&
      (showNotes || message.type !== 'conversation:wrapup_message'),
  );
  const listIsEmpty = supportedMessages.length === 0;

  return (
    <div className={styles.root}>
      {listIsEmpty ? (
        <div>This conversation has no messages.</div>
      ) : (
        supportedMessages.map((_message: Readonly<SupportedMessage>, index) => {
          const message = { ..._message };
          const own = message.author?.id === currentUserId;
          const isAgent = message.author?.id !== requesterId;
          const internal = message.type === 'conversation:wrapup_message';
          const isTranscript = Boolean(
            (typeof message.is_transcript === 'string' &&
              (message.is_transcript as string).toLowerCase() === 'true') ||
            message.is_transcript === true,
          );

          const nameMenu = isTranscript ? (
            <Badge size="small" variant="info">
              Transcribed
            </Badge>
          ) : undefined;

          const nameDetails =
            showRecipients && hasEmailRecipients(message) ? (
              <ConversationPreviewRecipients message={message} />
            ) : undefined;

          return (
            <TextMessage
              key={message.id || index}
              own={own}
              isAgent={isAgent}
              internal={internal}
              message={message}
              author={message.author}
              date={isTranscript ? undefined : message.created_at || ''}
              nameMenu={nameMenu}
              nameDetails={nameDetails}
              removeGutter
            />
          );
        })
      )}
    </div>
  );
};
